import React, { useState, useEffect, useCallback } from "react";
import {
  BarChart3,
  Bot,
  Users,
  MessageSquare,
  TrendingUp,
  Calendar,
  RefreshCw,
  Loader2,
  AlertCircle,
} from "lucide-react";
import {
  fetchClaw9Accounts,
  fetchClaw9Conversations,
  fetchClaw9Messages,
} from "../api/claw9";

// Channel configuration
const CHANNELS = {
  zalo: { label: "Zalo", color: "bg-blue-500" },
  messenger: { label: "Messenger", color: "bg-indigo-500" },
  facebook: { label: "Facebook", color: "bg-indigo-500" },
  telegram: { label: "Telegram", color: "bg-sky-500" },
  instagram: { label: "Instagram", color: "bg-purple-500" },
  whatsapp: { label: "WhatsApp", color: "bg-emerald-500" },
};

// Time filter options
const TIME_FILTERS = [
  { value: "today", label: "Today", labelVi: "Hôm nay" },
  { value: "7days", label: "Last 7 days", labelVi: "7 ngày qua" },
  { value: "30days", label: "Last 30 days", labelVi: "30 ngày qua" },
  { value: "all", label: "All time", labelVi: "Tất cả" },
];

// Stat card component
const StatCard = ({ icon: Icon, label, value, subValue, color = "text-[#ff4d4d]", loading }) => (
  <div className="bg-white/5 border border-white/10 rounded-xl p-5">
    <div className="flex items-center justify-between mb-3">
      <div className={`p-2 rounded-lg ${color.replace("text-", "bg-")}/20`}>
        <Icon className={`w-5 h-5 ${color}`} />
      </div>
    </div>
    <div className="space-y-1">
      {loading ? (
        <div className="h-8 w-20 bg-white/10 rounded animate-pulse" />
      ) : (
        <p className="text-2xl font-bold text-white">{value?.toLocaleString() || 0}</p>
      )}
      <p className="text-sm text-white/50">{label}</p>
      {subValue && (
        <p className="text-xs text-white/40">{subValue}</p>
      )}
    </div>
  </div>
);

// Channel breakdown bar
const ChannelBar = ({ channel, count, total, loading }) => {
  const config = CHANNELS[channel] || { label: channel, color: "bg-gray-500" };
  const percentage = total > 0 ? (count / total) * 100 : 0;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-sm">
        <span className="text-white/70">{config.label}</span>
        {loading ? (
          <div className="h-4 w-12 bg-white/10 rounded animate-pulse" />
        ) : (
          <span className="text-white font-medium">{count.toLocaleString()}</span>
        )}
      </div>
      <div className="h-2 bg-white/10 rounded-full overflow-hidden">
        <div
          className={`h-full ${config.color} transition-all duration-500`}
          style={{ width: loading ? "0%" : `${percentage}%` }}
        />
      </div>
    </div>
  );
};

/**
 * Claw9Analytics Component
 * Displays analytics for AI message responses
 */
const Claw9Analytics = () => {
  const [accountId, setAccountId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [timeFilter, setTimeFilter] = useState("7days");

  // Analytics data
  const [stats, setStats] = useState({
    totalAIResponses: 0,
    totalUserMessages: 0,
    totalConversations: 0,
    channelBreakdown: {},
    dailyStats: [],
  });

  // Initialize account
  useEffect(() => {
    const init = async () => {
      try {
        const result = await fetchClaw9Accounts();
        const accounts = result.accounts || [];
        if (accounts.length > 0) {
          setAccountId(accounts[0].account_id);
        } else {
          setError("No accounts found");
          setIsLoading(false);
        }
      } catch (err) {
        console.error("[Analytics] Init error:", err);
        setError(err.message);
        setIsLoading(false);
      }
    };
    init();
  }, []);

  // Get date range based on filter
  const getDateRange = useCallback(() => {
    const now = new Date();
    let startDate = null;

    switch (timeFilter) {
      case "today":
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        break;
      case "7days":
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case "30days":
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      default:
        startDate = null;
    }

    return { startDate, endDate: now };
  }, [timeFilter]);

  // Fetch and calculate analytics
  const fetchAnalytics = useCallback(async () => {
    if (!accountId) return;

    setIsRefreshing(true);
    setError(null);

    try {
      const { startDate } = getDateRange();

      // Fetch conversations
      const conversationsResult = await fetchClaw9Conversations({
        accountId,
        limit: 100,
      });

      const conversations = conversationsResult.conversations || [];

      let totalAI = 0;
      let totalUser = 0;
      const channelCounts = {};
      const dailyCounts = {};

      // Process each conversation
      for (const conv of conversations) {
        const channel = conv.channel || "unknown";

        // Initialize channel count
        if (!channelCounts[channel]) {
          channelCounts[channel] = { ai: 0, user: 0 };
        }

        try {
          // Fetch messages for this conversation
          const messagesResult = await fetchClaw9Messages(channel, conv.thread_id, {
            accountId,
            limit: 100,
          });

          const messages = messagesResult.messages || [];

          // Count messages
          for (const msg of messages) {
            const msgDate = new Date(msg.created_at);

            // Apply time filter
            if (startDate && msgDate < startDate) continue;

            const dateKey = msgDate.toISOString().split("T")[0];

            if (!dailyCounts[dateKey]) {
              dailyCounts[dateKey] = { ai: 0, user: 0 };
            }

            // Check sender type - bot/agent = AI response
            if (msg.sender_type === "bot" || msg.sender_type === "agent") {
              totalAI++;
              channelCounts[channel].ai++;
              dailyCounts[dateKey].ai++;
            } else {
              totalUser++;
              channelCounts[channel].user++;
              dailyCounts[dateKey].user++;
            }
          }
        } catch (msgErr) {
          console.warn(`[Analytics] Failed to fetch messages for ${conv.thread_id}:`, msgErr);
        }
      }

      // Convert daily counts to sorted array
      const dailyStats = Object.entries(dailyCounts)
        .map(([date, counts]) => ({ date, ...counts }))
        .sort((a, b) => a.date.localeCompare(b.date));

      setStats({
        totalAIResponses: totalAI,
        totalUserMessages: totalUser,
        totalConversations: conversations.length,
        channelBreakdown: channelCounts,
        dailyStats,
      });
    } catch (err) {
      console.error("[Analytics] Fetch error:", err);
      setError(err.message || "Failed to load analytics");
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [accountId, getDateRange]);

  // Load analytics when account is ready
  useEffect(() => {
    if (accountId) {
      fetchAnalytics();
    }
  }, [accountId, timeFilter, fetchAnalytics]);

  // Calculate totals for channel breakdown
  const totalChannelMessages = Object.values(stats.channelBreakdown).reduce(
    (sum, ch) => sum + ch.ai,
    0
  );

  return (
    <div className="h-full flex flex-col bg-[#0d1424] rounded-xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-[#ff4d4d]/20 to-orange-500/20 rounded-lg">
            <BarChart3 className="w-5 h-5 text-[#ff4d4d]" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-white">Analytics</h2>
            <p className="text-xs text-white/50">AI Response Statistics</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Time Filter */}
          <div className="flex bg-white/5 rounded-lg p-1">
            {TIME_FILTERS.map((filter) => (
              <button
                key={filter.value}
                onClick={() => setTimeFilter(filter.value)}
                className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                  timeFilter === filter.value
                    ? "bg-[#ff4d4d] text-white"
                    : "text-white/50 hover:text-white"
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>

          {/* Refresh Button */}
          <button
            onClick={fetchAnalytics}
            disabled={isRefreshing}
            className="p-2 text-white/50 hover:text-white hover:bg-white/10 rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-5 h-5 ${isRefreshing ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="mx-4 mt-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          <span>{error}</span>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-auto p-4 space-y-6">
        {/* Main Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard
            icon={Bot}
            label="AI Responses"
            value={stats.totalAIResponses}
            subValue={`${((stats.totalAIResponses / (stats.totalAIResponses + stats.totalUserMessages || 1)) * 100).toFixed(1)}% of total`}
            color="text-purple-400"
            loading={isLoading}
          />
          <StatCard
            icon={Users}
            label="User Messages"
            value={stats.totalUserMessages}
            color="text-blue-400"
            loading={isLoading}
          />
          <StatCard
            icon={MessageSquare}
            label="Conversations"
            value={stats.totalConversations}
            subValue={`${(stats.totalAIResponses / (stats.totalConversations || 1)).toFixed(1)} AI msgs/conv`}
            color="text-[#ff4d4d]"
            loading={isLoading}
          />
        </div>

        {/* Channel Breakdown */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-5">
          <h3 className="text-sm font-medium text-white mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-[#ff4d4d]" />
            AI Responses by Channel
          </h3>
          <div className="space-y-4">
            {Object.entries(stats.channelBreakdown).length === 0 && !isLoading ? (
              <p className="text-white/40 text-sm text-center py-4">No data available</p>
            ) : (
              Object.entries(stats.channelBreakdown)
                .sort((a, b) => b[1].ai - a[1].ai)
                .map(([channel, counts]) => (
                  <ChannelBar
                    key={channel}
                    channel={channel}
                    count={counts.ai}
                    total={totalChannelMessages}
                    loading={isLoading}
                  />
                ))
            )}
          </div>
        </div>

        {/* Daily Trend */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-5">
          <h3 className="text-sm font-medium text-white mb-4 flex items-center gap-2">
            <Calendar className="w-4 h-4 text-[#ff4d4d]" />
            Daily AI Responses
          </h3>
          {isLoading ? (
            <div className="h-40 flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-white/30" />
            </div>
          ) : stats.dailyStats.length === 0 ? (
            <p className="text-white/40 text-sm text-center py-8">No data available</p>
          ) : (
            <div className="space-y-2">
              {/* Simple bar chart */}
              <div className="flex items-end gap-1 h-32">
                {stats.dailyStats.slice(-14).map((day, index) => {
                  const maxAI = Math.max(...stats.dailyStats.map((d) => d.ai), 1);
                  const height = (day.ai / maxAI) * 100;

                  return (
                    <div
                      key={day.date}
                      className="flex-1 flex flex-col items-center group"
                    >
                      <div className="relative w-full">
                        {/* Tooltip */}
                        <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 hidden group-hover:block z-10">
                          <div className="bg-[#1a1f2e] border border-white/10 rounded-lg px-2 py-1 text-xs whitespace-nowrap">
                            <p className="text-white font-medium">{day.ai} AI</p>
                            <p className="text-white/50">{day.date}</p>
                          </div>
                        </div>
                        {/* Bar */}
                        <div
                          className="w-full bg-gradient-to-t from-[#ff4d4d] to-[#ff6b6b] rounded-t transition-all duration-300 hover:opacity-80"
                          style={{ height: `${Math.max(height, 4)}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* X-axis labels */}
              <div className="flex gap-1 text-[10px] text-white/30">
                {stats.dailyStats.slice(-14).map((day, index) => (
                  <div key={day.date} className="flex-1 text-center truncate">
                    {index === 0 || index === stats.dailyStats.slice(-14).length - 1
                      ? new Date(day.date).toLocaleDateString(undefined, { month: "short", day: "numeric" })
                      : ""}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Response Rate Card */}
        <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-xl p-5">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-white mb-1">AI Response Rate</h3>
              <p className="text-xs text-white/50">
                Percentage of messages handled by AI
              </p>
            </div>
            <div className="text-right">
              {isLoading ? (
                <div className="h-10 w-20 bg-white/10 rounded animate-pulse" />
              ) : (
                <>
                  <p className="text-3xl font-bold text-purple-400">
                    {stats.totalAIResponses + stats.totalUserMessages > 0
                      ? ((stats.totalAIResponses / (stats.totalAIResponses + stats.totalUserMessages)) * 100).toFixed(1)
                      : 0}%
                  </p>
                  <p className="text-xs text-white/50">
                    {stats.totalAIResponses} of {stats.totalAIResponses + stats.totalUserMessages} messages
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Claw9Analytics;
